import { GetSpotifyTrackSearchResponse, spotifyQueries } from '@round/api';
import { InfiniteData, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useSpotifySongs } from '@round/platforms/spotify';
import { mapToItem, SpotifyTrackItem } from './useSpotifyTrackCombobox';
import { uniqBy } from 'lodash';

const tracksQueryKey = spotifyQueries.trackSearch.keys.lists;

export function useSelectedSpotifyTrackItems(selectedSongSpotifyIds: string[]) {
    const queryClient = useQueryClient();

    // We use the tracks endpoint (which uses the spotify API) to display track options, but we cannot fetch tracks by ID, only by search term.
    // After a track is selected, it's best for us to rely on our own songs API as the data source so we can e.g. populate from URL state
    // so we will primarily fetch the selected songs from there.
    //
    // However, the cache of fetched track data from recent options searches will contain relevant data for a while, so we will use it here to reduce unnecessary calls to the songs EP,
    // remove the need to manage additional local state for selected tracks,
    // and to avoid scenarios where the task to save songs from options searches to our DB is delayed and no data is present yet.

    // Get all cached tracks data
    const trackQueryCacheData = queryClient
        .getQueriesData<InfiniteData<GetSpotifyTrackSearchResponse['data']> | GetSpotifyTrackSearchResponse['data']>({
            queryKey: tracksQueryKey(),
        })
        .map((cacheEntry) => cacheEntry[1]);

    // Map to just the tracks
    const tracksInCache = trackQueryCacheData
        .map((data) => {
            if (!data) {
                return [];
            }

            if (data && 'pages' in data) {
                return data.pages.flatMap((p) => p.results);
            }

            return data.results;
        })
        .flat();

    const relevantTracksInCache = tracksInCache.filter((t) => selectedSongSpotifyIds.includes(t.spotify_id));
    const relevantTracksInCacheIds = relevantTracksInCache.map((t) => t.spotify_id);

    const spotifySongsNotInTrackCacheIds = selectedSongSpotifyIds.filter(
        (id) => !relevantTracksInCacheIds.includes(id)
    );
    const { data: songData, isFetching: isSongDataFetching } = useSpotifySongs(
        { spotify_id: spotifySongsNotInTrackCacheIds.join(','), page_size: spotifySongsNotInTrackCacheIds.length },
        { enabled: !!spotifySongsNotInTrackCacheIds.length, placeholderData: keepPreviousData }
    );

    const items: SpotifyTrackItem[] = uniqBy(
        [
            ...relevantTracksInCache.map(mapToItem),
            ...(songData?.results.map((song) => ({
                value: song.spotify_id,
                label: song.title,
                artists: song.artists,
                image_url: song.image_url,
            })) ?? []),
        ],
        'value'
    );

    return {
        items,
        isFetching: isSongDataFetching,
    };
}
